import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html, TemplateResult } from 'lit'
import { property } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { fromEvent, takeUntil } from 'rxjs'
import { tableBodyCell } from './cell'
import type { TableColumn, TableDensity } from './table'

@schmancyElement('schmancy-table-row')
export class SchmancyTableRow<T extends Record<string, any> = any> extends SchmancyElement {
	static styles = [
		css`
			/* No padding or margin here, ever: for normal declarations the OUTER
			   encapsulation context wins on a host (CSS Cascade §6.1, Context),
			   and Tailwind v4 preflight's universal padding:0 sits in the
			   table's shadow tree — so a padding-block declared here renders
			   zero at any specificity, which is how these rows came to be 21px
			   tall under a 44px header. The row's height is its cells' business.

			   The state tint travels as a custom property painted on a
			   shadow-internal element rather than on this host, for the same
			   cascade reason worn the other way round: a consumer's own
			   background utility on <schmancy-table-row> silently defeated a
			   :host() background-color however specific it was. Overriding
			   --schmancy-table-row-state-layer on the host is the supported way
			   to theme these states. */
			:host {
				display: grid;
				grid-column: 1 / -1;
				grid-template-columns: subgrid;
				position: relative;
				/* So the state layer's negative z-index resolves against this row
				   and not the nearest ancestor stacking context, where it would
				   paint behind the table and render nothing. */
				isolation: isolate;
				border-radius: var(--schmancy-radius-lg, 0.625rem); /* mirrors --radius-lg (rounded-lg) */
				outline: none;
				--schmancy-table-row-state-layer: transparent;
			}
			/* Interactivity is the presence of a name — the table hands one down
			   only when its consumer gave it a rowLabel. A row nobody can click
			   takes no tab stop, shows no pointer, and claims nothing. */
			:host([aria-label]) {
				cursor: pointer;
			}
			:host(:hover) {
				--schmancy-table-row-state-layer: color-mix(in srgb, var(--schmancy-sys-color-surface-on) 4%, transparent);
			}
			:host([aria-label]:hover) {
				--schmancy-table-row-state-layer: color-mix(in srgb, var(--schmancy-sys-color-surface-on) 8%, transparent);
			}
			:host([aria-label]:active) {
				--schmancy-table-row-state-layer: color-mix(in srgb, var(--schmancy-sys-color-surface-on) 12%, transparent);
			}
			:host([selected]) {
				--schmancy-table-row-state-layer: color-mix(
					in srgb,
					var(--schmancy-sys-color-secondary-container) 30%,
					transparent
				);
			}
			:host([selected]:hover) {
				--schmancy-table-row-state-layer: color-mix(
					in srgb,
					var(--schmancy-sys-color-secondary-container) 38%,
					transparent
				);
			}
			/* Inset, so the ring is drawn inside the row's own band and the rows
			   above and below do not have to leave room for it. */
			:host(:focus-visible) {
				outline: 2px solid var(--schmancy-sys-color-secondary-default);
				outline-offset: -2px;
				z-index: 1;
			}
		`,
	]

	@property({ type: Array, attribute: false })
	columns: TableColumn<T>[] = []

	@property({ type: Object, attribute: false })
	item!: T

	/** Row-height floor for this row's cells. Set by the table. */
	@property({ type: String })
	density: TableDensity = 'default'

	/**
	 * The row's accessible name, and the one signal that makes it interactive:
	 * with it the row takes a tab stop, shows a pointer, and activates on
	 * Enter/Space. Without it every row was a `role="button"` with an empty
	 * accessible name in three tables where nothing was clickable.
	 */
	@property({ type: String, reflect: true, attribute: 'aria-label' })
	label: string | null = null

	@property({ type: Boolean, reflect: true })
	selected = false

	// Enter/Space on a focusable non-button element fires no click of its own, so
	// a keyboard user could reach the row and never activate it. Dispatch the
	// same event a pointer would, once, so both paths land on the table's one
	// listener.
	private onKeydown = (e: KeyboardEvent): void => {
		if (!this.label) return
		if (e.key !== 'Enter' && e.key !== ' ') return
		// Held key: fire once only.
		if (e.repeat) return
		// composedPath()[0] is the deepest target: this host when the row itself
		// holds focus, or a cell's own control when one does — in which case that
		// control owns the key. Identity covers every such control, including the
		// <button> and <a> a tag allowlist misses.
		if (e.composedPath()[0] !== this) return
		e.preventDefault()
		this.dispatchEvent(new MouseEvent('click', { bubbles: true, composed: true, cancelable: true }))
	}

	connectedCallback(): void {
		super.connectedCallback()
		fromEvent<KeyboardEvent>(this, 'keydown').pipe(takeUntil(this.disconnecting)).subscribe(this.onKeydown)
	}

	protected updated(changed: Map<PropertyKey, unknown>): void {
		super.updated(changed)
		if (changed.has('label')) {
			if (this.label) this.setAttribute('tabindex', '0')
			else this.removeAttribute('tabindex')
		}
	}

	render(): TemplateResult {
		// The state layer is out of flow, so it is not a grid item and takes no
		// track; at a negative index inside this host's own stacking context it
		// paints above the row's ground and beneath every cell's ink. The cells
		// are direct shadow children with nothing between them and the parent's
		// column tracks, which is what lets `subgrid` resolve.
		return html`<div
				class="pointer-events-none absolute inset-0 -z-1 rounded-[inherit] bg-[var(--schmancy-table-row-state-layer)] transition-colors duration-200 ease-in-out"
			></div>${repeat(
				this.columns,
				column => String(column.key ?? column.name),
				column => tableBodyCell(column, this.item, this.density),
			)}`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-table-row': SchmancyTableRow
	}
}
